import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SyncIcon from '@mui/icons-material/Sync'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { listRdMembers, addRdMember, updateRdMemberRole, removeRdMember, listRoles, type Member, type Role } from '../../services/authApi'

interface MembersTabProps {
  rdId: string
}

export function MembersTab({ rdId }: MembersTabProps) {
  const [members, setMembers] = useState<Member[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [error, setError] = useState('')
  const [addOpen, setAddOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([listRdMembers(rdId), listRoles(rdId)])
      setMembers(m)
      setRoles(r)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    }
  }, [rdId])

  useEffect(() => { load() }, [load])

  async function handleRoleChange(userId: string, newRole: string) {
    try {
      await updateRdMemberRole(rdId, userId, { role: newRole })
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role')
    }
  }

  async function handleRemove(userId: string) {
    if (!confirm('Remove this member from the research domain?')) return
    try {
      await removeRdMember(rdId, userId)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member')
    }
  }

  const roleColor = (role: string) => {
    if (role === 'owner') return 'error'
    if (role === 'admin') return 'warning'
    if (role === 'analyst') return 'info'
    return 'default'
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Add Member
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Login Status</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map(m => (
              <TableRow key={m.userId} hover>
                <TableCell>{m.name || '—'}</TableCell>
                <TableCell>{m.email}</TableCell>
                <TableCell>
                  <code style={{ fontSize: '0.75rem' }}>{m.userId.slice(0, 12)}...</code>
                </TableCell>
                <TableCell>
                  {m.emailVerified === 1 ? (
                    <Tooltip title="Verified — this user has logged in via Cognito with a verified email."><CheckCircleIcon fontSize="small" color="success" /></Tooltip>
                  ) : (
                    <Tooltip title="Pending — pre-provisioned record. The user has not yet logged in via Cognito (or their email is unverified). A green check appears only after a real verified login."><SyncIcon fontSize="small" color="warning" /></Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={m.role}
                    onChange={e => handleRoleChange(m.userId, e.target.value)}
                    sx={{ minWidth: 120 }}
                    renderValue={v => <Chip label={v} size="small" color={roleColor(v)} variant="outlined" />}
                  >
                    {roles.map(r => (
                      <MenuItem key={r.name} value={r.name}>{r.name}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => handleRemove(m.userId)} title="Remove member">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {members.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No members
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddMemberDialog
        open={addOpen}
        roles={roles}
        onClose={() => setAddOpen(false)}
        onAdded={() => { setAddOpen(false); load() }}
        rdId={rdId}
      />
    </Box>
  )
}

function AddMemberDialog({ open, roles, onClose, onAdded, rdId }: {
  open: boolean; roles: Role[]; onClose: () => void; onAdded: () => void; rdId: string
}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [userId, setUserId] = useState('')
  const [role, setRole] = useState('analyst')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleAdd() {
    setError('')
    setLoading(true)
    try {
      await addRdMember(rdId, { email, role, userId: userId || undefined, name: name || undefined })
      setEmail(''); setName(''); setUserId('')
      onAdded()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add member')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Member</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth label="Email" value={email} required type="email"
          onChange={e => setEmail(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          fullWidth label="Name" value={name}
          onChange={e => setName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth label="Cognito User ID" value={userId}
          onChange={e => setUserId(e.target.value)}
          helperText="Optional — if blank, auto-generated or matched by email"
          sx={{ mb: 2 }}
        />
        <Select fullWidth value={role} onChange={e => setRole(e.target.value)} size="small">
          {roles.map(r => <MenuItem key={r.name} value={r.name}>{r.name} — {r.description}</MenuItem>)}
        </Select>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleAdd} disabled={loading || !email}>
          {loading ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
